import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Stack, Box } from '@mui/material';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  CartesianGrid,
  ComposedChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatShortDateTime, localizeDate } from '../../../utils/datetime';
import { TestInvocation } from '../../../types';
import RunsChartTooltip from './RunsChartTooltip';

const DISABLE_ANIMATION_THRESHOLD = 50;

const barColorMap = {
  fail: 'red',
  warn: 'orange',
  pass: 'green',
  error: 'red',
  skipped: 'gray1',
  no_data: 'gray1',
} as any;

const barHoverColorMap = {
  fail: 'rgba(255, 101, 91, 0.2)',
  warn: 'rgb(255, 166, 33, 0.2)',
  pass: 'rgba(66, 203, 154, 0.2)',
  error: 'rgba(255, 101, 91, 0.2)',
} as any;

const Wrapper = styled(Stack)`
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.common.gray3};
  width: 100%;
`;

const BarWrapper = styled(Stack)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: flex-end;
`;

const BarElement = styled(Box)<{
  width: number;
  height: number;
  status: string;
}>`
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  background: ${({ theme, status }) =>
    theme.palette.common[barColorMap[status]]};
  border: 1px solid transparent;
  border-radius: 4px 4px 0 0;
  &:hover {
    border: ${({ status }) => `3px solid ${barHoverColorMap[status]}`};
    border-bottom: none;
    box-sizing: content-box;
    border-radius: 8px 8px 0 0;
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
  }
`;

const renderTooltip = (props: any) => <RunsChartTooltip {...props} />;

const ChartBar = ({ x, y, height, width, payload }: any) => (
  <foreignObject x={x - 2.5} y={y - 3} height={height + 3} width={width + 6}>
    <BarWrapper direction="row">
      <BarElement status={payload.status} width={width} height={height} />
    </BarWrapper>
  </foreignObject>
);

type Props = {
  data: TestInvocation[];
  type: string;
};

const TestRunsChart = ({ data, type }: Props) => {
  const scrollContainerRef = useRef<any>();

  const isElementaryType = useMemo(() => type !== 'dbt_test', [type]);

  const normalizedData = useMemo(
    () =>
      Array.isArray(data)
        ? [...data]
            .sort(
              (a, b) =>
                dayjs(a.time_utc).valueOf() - dayjs(b.time_utc).valueOf(),
            )
            .map((item) => ({
              ...item,
              affected_rows: item.affected_rows || 0,
            }))
        : [],
    [data],
  );

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current?.scrollWidth;
    }
  }, [normalizedData.length]);

  return (
    <Wrapper direction="row">
      <ComposedChart
        data={normalizedData}
        margin={{ bottom: 46, top: 30, left: 14 }}
        height={isElementaryType ? 100 : 300}
        width={isElementaryType ? 16 : 80}
      >
        {!isElementaryType && (
          <YAxis
            axisLine={false}
            tickLine={false}
            tickMargin={6}
            dataKey="affected_rows"
            allowDecimals={false}
            tick={{ fontSize: 13 }}
            tickFormatter={(value) => Humanize.compactInteger(value)}
          />
        )}
      </ComposedChart>

      <Box
        sx={{ overflowX: 'auto', overflowY: 'hidden', flex: 1 }}
        ref={scrollContainerRef}
      >
        <ResponsiveContainer
          minWidth={normalizedData.length * 40}
          width="99%"
          height={isElementaryType ? 100 : 300}
        >
          <ComposedChart
            data={normalizedData}
            margin={{ top: 28, right: 20, bottom: 14 }}
          >
            {!isElementaryType && <CartesianGrid stroke="#E6E8EC" />}

            <XAxis
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              tickFormatter={(value) =>
                dayjs(value).isValid() && value !== 0
                  ? formatShortDateTime(localizeDate(value))
                  : ''
              }
              dataKey="time_utc"
              tick={{ fontSize: 11 }}
            />

            {!isElementaryType && (
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={6}
                dataKey="affected_rows"
                allowDecimals={false}
                tick={{ fontSize: 13 }}
                hide
                tickFormatter={(value) => Humanize.compactInteger(value)}
              />
            )}

            <Tooltip
              content={(props) =>
                renderTooltip({ ...props, floatTooltip: isElementaryType })
              }
              cursor={!isElementaryType}
              isAnimationActive={!isElementaryType}
              wrapperStyle={{
                outline: 0,
                visibility: isElementaryType ? 'collapse' : 'visible',
              }}
            />

            <Bar
              dataKey="affected_rows"
              barSize={10}
              minPointSize={14}
              activeBar={false}
              shape={<ChartBar />}
              isAnimationActive={
                !!(normalizedData.length < DISABLE_ANIMATION_THRESHOLD)
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Wrapper>
  );
};

export default TestRunsChart;
